import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ProgressBarModule } from 'primeng/progressbar';
import {
    DashboardService,
    RecommendationAuditDaily,
    RecommendationAuditDashboard,
    RecommendationAuditSalesperson,
    RecommendationAuditSummary
} from '../pages/service/dashboard.service';
import { canAccessRecommendationsAudit } from './recommendations-audit.access';

@Component({
    selector: 'app-recommendations-audit',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToolbarModule,
        ButtonModule,
        CardModule,
        TableModule,
        SelectButtonModule,
        ProgressSpinnerModule,
        ProgressBarModule
    ],
    templateUrl: './recommendations-audit.component.html',
    styleUrl: './recommendations-audit.component.scss'
})
export class RecommendationsAuditComponent implements OnInit {
    /** API clamps to max 90 days; default last 30 days for light aggregates. */
    readonly maxRangeDays = 90;

    fromDate = '';
    toDate = '';
    selectedView: 'month' | 'custom' = 'month';
    dateViewOptions = [
        { label: 'Last 30 days', value: 'month' },
        { label: 'Custom', value: 'custom' }
    ];

    summary: RecommendationAuditSummary | null = null;
    daily: RecommendationAuditDaily[] = [];
    bySalesperson: RecommendationAuditSalesperson[] = [];
    sourceMix: { label: string; count: number; pct: number }[] = [];

    loading = false;
    error: string | null = null;
    rangeHint: string | null = null;

    constructor(
        private readonly dashboardService: DashboardService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        if (!canAccessRecommendationsAudit()) {
            this.router.navigate(['/dashboard']);
            return;
        }
        this.applyMonthPreset();
        this.reload();
    }

    goToOrders(): void {
        this.router.navigate(['/Orders1']);
    }

    onDateViewChange(): void {
        if (this.selectedView === 'month') {
            this.applyMonthPreset();
            this.reload();
        }
    }

    onCustomDateChange(): void {
        this.selectedView = 'custom';
        this.reload();
    }

    reload(): void {
        this.error = null;
        this.rangeHint = null;
        if (!this.fromDate || !this.toDate) {
            return;
        }
        if (this.toDate < this.fromDate) {
            this.error = 'To date must be on or after From date.';
            return;
        }
        const from = new Date(this.fromDate);
        const to = new Date(this.toDate);
        const days = Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1;
        if (days > this.maxRangeDays) {
            this.rangeHint = `Range limited to ${this.maxRangeDays} days on the server for performance. Showing the latest ${this.maxRangeDays} days ending ${this.toDate}.`;
        }

        this.loading = true;
        this.dashboardService.getRecommendationAuditDashboard(this.fromDate, this.toDate).subscribe({
            next: (dash: RecommendationAuditDashboard) => {
                this.summary = dash?.summary || null;
                this.daily = dash?.daily || [];
                this.bySalesperson = dash?.bySalesperson || [];
                this.sourceMix = this.buildSourceMix(this.summary);
                this.loading = false;
            },
            error: (err) => {
                this.loading = false;
                this.summary = null;
                this.daily = [];
                this.bySalesperson = [];
                this.sourceMix = [];
                this.error = err?.error?.message || err?.message || 'Failed to load recommendations dashboard.';
            }
        });
    }

    private applyMonthPreset(): void {
        const today = new Date();
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        this.fromDate = formatDate(from, 'yyyy-MM-dd', 'en');
        this.toDate = formatDate(today, 'yyyy-MM-dd', 'en');
    }

    private buildSourceMix(s: RecommendationAuditSummary | null): { label: string; count: number; pct: number }[] {
        if (!s || !s.totalAuditedLines) {
            return [];
        }
        const total = s.totalAuditedLines;
        const rows = [
            { label: 'Recommendation select', count: s.recommendationSelectCount || 0 },
            { label: 'Manual', count: s.manualCount || 0 },
            { label: 'Copy', count: s.copyCount || 0 },
            { label: 'Excel', count: s.excelCount || 0 },
            { label: 'Other', count: s.otherSourceCount || 0 }
        ];
        return rows
            .filter((r) => r.count > 0)
            .map((r) => ({
                ...r,
                pct: Math.round((r.count * 10000) / total) / 100
            }));
    }

    overlapPct(trueCount: number, falseCount: number): number {
        const scored = (trueCount || 0) + (falseCount || 0);
        if (!scored) {
            return 0;
        }
        return Math.round((trueCount * 10000) / scored) / 100;
    }
}
